import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiExternalLink, FiUpload, FiFileText, FiDownload, FiEdit2, FiTrash2, FiSliders } from 'react-icons/fi';
import { toast } from '../../Toast';
import Toggle from '../../Toggle';
import RemoveItemModal from '../RemoveItemModal';
import { copyText } from '../../clientSettings/widgets/shared';
import { BackLink, HubHeader, HubTip, SearchBox, FilterChips, IconButton, hubPath, useHubSite } from '../shared';
import { GripIcon } from './BoardManagement';
import { FilePick, DOC_TYPES } from './governance/FileBox';
import UploadDocumentModal, { Upload } from './governance/UploadDocumentModal';
import ManageSectionsModal from './governance/ManageSectionsModal';
import EditDocumentModal from './governance/EditDocumentModal';
import { DOCS, GovDoc, GovSection, OFF_PAGE, OTHER, SECTIONS, shortDate, sizeText, todayIso } from './governance/governanceData';

const STATEMENT_CODE = '[investi_governance block="statement"]';

// Website Hub › Website content › Corporate governance (spec 405:10406)
const Governance: React.FC = () => {
    const history = useHistory();
    const { host, url } = useHubSite();
    const [sections, setSections] = useState<GovSection[]>(SECTIONS);
    const [docs, setDocs] = useState<GovDoc[]>(DOCS);
    const [q, setQ] = useState('');
    const [chip, setChip] = useState('all');
    const [statement, setStatement] = useState({ file: 'Corporate-Governance-Statement-2026.pdf', sizeKb: 420, approved: '30 Jun 2026', year: 'FY2026', visible: true });
    const [uploading, setUploading] = useState(false);
    const [managing, setManaging] = useState(false);
    const [editing, setEditing] = useState<GovDoc | null>(null);
    const [removing, setRemoving] = useState<GovDoc | null>(null);
    const [dragging, setDragging] = useState<string | null>(null);
    const moved = useRef(false);

    const counts = docs.reduce<Record<string, number>>((acc, d) => ({ ...acc, [d.section]: (acc[d.section] ?? 0) + 1 }), {});
    const total = docs.length + OFF_PAGE.all;
    const chips = [{ value: 'all', label: 'All', count: total }, ...sections.map(s => ({ value: s.id, label: s.label, count: (counts[s.id] ?? 0) + (OFF_PAGE[s.id] ?? 0) }))];
    const sectionName = (id: string) => sections.find(s => s.id === id)?.name ?? OTHER.name;
    const shown = docs.filter(d => (chip === 'all' || d.section === chip) && d.name.toLowerCase().includes(q.trim().toLowerCase()));

    const toggle = (d: GovDoc) => {
        setDocs(xs => xs.map(x => x.id === d.id ? { ...x, visible: !x.visible } : x));
        toast.info(`Preview only: ${d.name} ${d.visible ? 'hidden' : 'shown'} here, the website wasn’t changed`);
    };
    // live reorder while dragging over another row in the same section
    const dragOver = (e: React.DragEvent, over: GovDoc) => {
        e.preventDefault();
        if (!dragging || dragging === over.id) return;
        setDocs(xs => {
            const from = xs.findIndex(x => x.id === dragging);
            const to = xs.findIndex(x => x.id === over.id);
            if (from < 0 || xs[from].section !== over.section) return xs;
            moved.current = true;
            const next = xs.filter(x => x.id !== dragging);
            next.splice(to, 0, xs[from]);
            return next;
        });
    };
    const dragEnd = () => {
        setDragging(null);
        if (!moved.current) return;
        moved.current = false;
        toast.info('Preview only: order changed here, the website wasn’t changed');
    };

    // same name in the same section = a new version of that document (keeps its link)
    const upload = (u: Upload) => {
        const existing = docs.find(d => d.section === u.section && d.name.toLowerCase() === u.name.toLowerCase());
        const file = { file: u.picked.file, kind: u.picked.kind, sizeKb: u.picked.sizeKb, updated: todayIso(), visible: u.visible, uploadedBy: 'you' };
        if (existing) setDocs(xs => xs.map(x => x.id === existing.id ? { ...x, ...file } : x));
        else setDocs(xs => {
            const lastInSection = xs.map(x => x.section).lastIndexOf(u.section);
            const next = [...xs];
            next.splice(lastInSection < 0 ? xs.length : lastInSection + 1, 0, { id: `d${Date.now()}`, name: u.name, section: u.section, ...file });
            return next;
        });
        setUploading(false);
        toast.info(existing
            ? `Preview only: new version of ${u.name} here — same link on the website, nothing was uploaded`
            : `Preview only: ${u.name} added here${u.visible ? '' : ' as hidden'}, nothing was uploaded`);
    };

    // documents in a deleted section move to "Other"
    const saveSections = (next: GovSection[]) => {
        const kept = new Set(next.map(s => s.id));
        const orphaned = docs.some(d => !kept.has(d.section));
        setDocs(xs => xs.map(x => kept.has(x.section) ? x : { ...x, section: OTHER.id }));
        setSections(orphaned && !kept.has(OTHER.id) ? [...next, OTHER] : next);
        if (!kept.has(chip)) setChip('all');
        setManaging(false);
        toast.info('Preview only: sections saved here, the website wasn’t changed');
    };

    const saveDoc = (d: GovDoc) => {
        setDocs(xs => xs.map(x => x.id === d.id ? d : x));
        setEditing(null);
        toast.info(`Preview only: ${d.name} saved here, the website wasn’t changed`);
    };

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="Corporate Governance" sub={`Charters, policies and statements shown on ${host}/about-us/corporate-governance/`}>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/corporate-governance')}><FiCode /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url('/about-us/corporate-governance/')} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>
            <button type="button" className="btn-primary with-icon" onClick={() => setUploading(true)}><FiUpload /> Upload Document</button>
        </HubHeader>

        <section className="card hub-statement">
            <div className="hub-statement-head">
                <div className="text">
                    <h2>Corporate Governance Statement</h2>
                    <span className="hint">Its own field, separate from the document list. It sits above the list in the widget, and has its own embed code.</span>
                </div>
                <span className="hub-statement-toggle">
                    Show on the Website
                    <Toggle checked={statement.visible} onChange={v => setStatement(s => ({ ...s, visible: v }))} label="Show the statement on the website" />
                </span>
            </div>
            <div className="hub-statement-fields">
                <div className="field">
                    <span className="hub-caps">Document</span>
                    <div className="value-box subtle">
                        <span>{statement.file} · {sizeText(statement.sizeKb)}</span>
                        <FilePick className="hub-small-btn" accept={DOC_TYPES} onPick={f => setStatement(s => ({ ...s, file: f.file, sizeKb: f.sizeKb }))}>Replace</FilePick>
                    </div>
                </div>
                <label className="field">
                    <span className="hub-caps">Approved / updated</span>
                    <input className="subtle" value={statement.approved} onChange={e => setStatement(s => ({ ...s, approved: e.target.value }))} />
                </label>
                <label className="field">
                    <span className="hub-caps">Financial year</span>
                    <input className="subtle" value={statement.year} onChange={e => setStatement(s => ({ ...s, year: e.target.value }))} />
                </label>
            </div>
            <div className="hub-statement-foot">
                <span>The Appendix 4G is uploaded to the document list below, under Policies &amp; Procedures.</span>
                <button type="button" className="hub-small-btn" onClick={() => copyText(STATEMENT_CODE, 'embed code')}>Embed Code</button>
                <button type="button" className="hub-small-btn dark" onClick={() => toast.info('Preview only: the statement was saved here, the website wasn’t changed')}>Save</button>
            </div>
        </section>

        <section className="card hub-list hub-gov-list">
            <div className="hub-toolbar">
                <SearchBox value={q} onChange={setQ} placeholder="Search documents" />
                <FilterChips chips={chips} value={chip} onChange={setChip} />
                <button type="button" className="btn-outline with-icon" onClick={() => setManaging(true)}><FiSliders /> Manage Sections</button>
            </div>

            <div className="hub-table">
                <div className="tr th"><span className="item">Document</span><span className="hub-gov-section">Section</span><span className="hub-gov-updated">Updated</span><span className="hub-gov-live">On website</span><span className="act" /></div>
                {shown.map(d => <div key={d.id} className={`tr${dragging === d.id ? ' hub-dragging' : ''}`} draggable
                    onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragging(d.id); }}
                    onDragOver={e => dragOver(e, d)} onDragEnd={dragEnd}>
                    <span className="item">
                        <span className="hub-grip" title="Drag to reorder within the section"><GripIcon /></span>
                        <span className="hub-doc-icon"><FiFileText /></span>
                        <span className="t"><strong>{d.name}</strong><span>{d.kind} · {sizeText(d.sizeKb)}</span></span>
                    </span>
                    <span className="hub-gov-section">{sectionName(d.section)}</span>
                    <span className="hub-gov-updated">{shortDate(d.updated)}</span>
                    <span className="hub-gov-live"><Toggle checked={d.visible} onChange={() => toggle(d)} label={`Show ${d.name} on the website`} /></span>
                    <span className="act">
                        <IconButton label="Download" onClick={() => toast.info('Preview only: there’s no file behind the sample documents')}><FiDownload /></IconButton>
                        <IconButton label="Edit" onClick={() => setEditing(d)}><FiEdit2 /></IconButton>
                        <IconButton label="Remove" danger onClick={() => setRemoving(d)}><FiTrash2 /></IconButton>
                    </span>
                </div>)}
                {shown.length === 0 && <p className="hub-empty">Nothing matches — try another filter.</p>}
            </div>

            <div className="hub-pager">
                <span>{total} documents · drag the handle to reorder within a section</span>
                <button type="button" className="btn-outline" disabled>Previous</button>
                <button type="button" className="btn-outline" onClick={() => toast.info('Preview only: the sample has one page')}>Next</button>
            </div>
        </section>

        <HubTip>Uploading a new version of a document keeps the same link on the website, so old bookmarks and Google results still work.</HubTip>

        {uploading && <UploadDocumentModal sections={sections} onClose={() => setUploading(false)} onUpload={upload} />}
        {managing && <ManageSectionsModal sections={sections} counts={counts} onClose={() => setManaging(false)} onSave={saveSections} />}
        {editing && <EditDocumentModal doc={editing} sections={sections} onClose={() => setEditing(null)} onSave={saveDoc}
            onDelete={() => { setRemoving(editing); setEditing(null); }} />}
        {removing && <RemoveItemModal noun="Document" name={removing.name} onClose={() => setRemoving(null)}
            onHide={() => { setDocs(xs => xs.map(x => x.id === removing.id ? { ...x, visible: false } : x)); setRemoving(null); toast.info('Preview only: hidden here, the website wasn’t changed'); }}
            onRemove={() => { setDocs(xs => xs.filter(x => x.id !== removing.id)); setRemoving(null); toast.info('Preview only: removed here, the website wasn’t changed'); }} />}
    </>;
};

export default Governance;
